import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Calendar, 
  Search, 
  Plus,
  Clock,
  Video,
  Phone,
  MapPin,
  CheckCircle,
  XCircle,
  Loader,
  FileText,
  ExternalLink
} from 'lucide-react'
import { apiClient } from '@/lib/api'

// Helper function to safely format date and time
const formatDateTime = (date?: string, time?: string) => {
  if (!date) return { date: 'No date', time: 'No time' }
  if (!time) return { date: date, time: 'No time' }
  return { date, time }
}

interface Interview {
  id: string
  title: string
  name?: string
  interview_type: string
  format: 'video' | 'phone' | 'in-person'
  stage: 'screening' | 'technical' | 'behavioral' | 'final' | 'culture-fit'
  scheduled_at: string
  duration_minutes: number
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled' | 'no-show'
  meeting_url?: string
  overall_rating?: number
  recommendation?: string
  notes?: string
  is_draft?: boolean
  candidate: {
    id: number
    first_name: string
    last_name: string
    email: string
  }
  position: {
    id: number
    title: string
  }
  interviewer: {
    first_name: string
    last_name: string
  }
  created_at: string
  // Legacy properties for backward compatibility
  candidateName?: string
  candidateEmail?: string
  candidateId?: string
  positionId?: string
  type?: string
  date?: string
  time?: string
  duration?: number
  interviewers?: string[]
  location?: string
  meetingLink?: string
  score?: number
  feedback?: string
  createdAt?: string
  shareType?: 'created' | 'shared_individual' | 'shared_team'
  sharedBy?: string
  sharedByName?: string
  sharedTeamName?: string
  isCreatedByMe?: boolean
}


export default function InterviewsPage() {
  const navigate = useNavigate()
  const [interviews, setInterviews] = useState<Interview[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')

  useEffect(() => {
    loadInterviews()
  }, [statusFilter, typeFilter, searchQuery])

  // Listen for interview scheduling events to refresh the list
  useEffect(() => {
    const handleInterviewScheduled = () => {
      loadInterviews() // Refresh the interviews list
    }

    window.addEventListener('interviewScheduled', handleInterviewScheduled)
    
    return () => {
      window.removeEventListener('interviewScheduled', handleInterviewScheduled)
    }
  }, [])

  const loadInterviews = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getInterviews({
        status: statusFilter,
        search: searchQuery,
        limit: 1000  // Load all interviews without pagination limit
      })

      if (response.success && response.data) {
        // Handle different API response structures
        const interviewsData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).interviews || []
        
        // Ensure we have valid interview data and normalize it
        if (Array.isArray(interviewsData)) {
          // Normalize the data to match our interface
          const normalizedInterviews = interviewsData.map((interview: any) => ({
            ...interview,
            // Add backward compatibility properties
            candidateName: `${interview.candidate?.first_name || ''} ${interview.candidate?.last_name || ''}`.trim() || interview.candidateName,
            candidateEmail: interview.candidate?.email || interview.candidateEmail,
            candidateId: interview.candidate?.id?.toString() || interview.candidateId,
            positionId: interview.position?.id?.toString() || interview.positionId,
            type: interview.format || interview.type,
            date: interview.scheduled_at ? interview.scheduled_at.split('T')[0] : interview.date,
            time: interview.scheduled_at ? interview.scheduled_at.split('T')[1]?.substring(0, 5) : interview.time,
            duration: interview.duration_minutes || interview.duration,
            meetingLink: interview.meeting_url || interview.meetingLink,
            feedback: interview.notes || interview.feedback,
            createdAt: interview.created_at || interview.createdAt
          }))
          
          setInterviews(normalizedInterviews as Interview[])
        } else {
          console.log('API returned invalid data structure')
          setInterviews([])
        }
      } else {
        console.log('API failed')
        setInterviews([])
      }
    } catch (err) {
      console.error('Failed to load interviews:', err)
      setInterviews([])
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string, isDraft?: boolean): string => {
    if (isDraft) {
      return 'bg-orange-100 text-orange-800 border border-orange-200'
    }
    
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800'
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      case 'no-show':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusDisplay = (status: string, isDraft?: boolean): string => {
    if (isDraft) {
      return 'draft'
    }
    return status
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="w-4 h-4" />
      case 'phone':
        return <Phone className="w-4 h-4" />
      case 'in-person':
        return <MapPin className="w-4 h-4" />
      default:
        return <Calendar className="w-4 h-4" />
    }
  }

  const handleScheduleNew = (): void => {
    window.dispatchEvent(new CustomEvent('openScheduleModal'))
  }

  const handleScheduleFullPage = (): void => {
    navigate('/recruiter/interviews/schedule')
  }

  const handleRowClick = (interviewId: string): void => {
    // Navigate to interview response/details page
    navigate(`/recruiter/interviews/${interviewId}`)
  }

  const formatDateTime = (date: string, time: string) => {
    const dateTime = new Date(`${date}T${time}`)
    return {
      date: dateTime.toLocaleDateString(),
      time: dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }

  const filteredInterviews = (interviews || []).filter(interview => {
    const candidateName = interview.candidateName || `${interview.candidate?.first_name || ''} ${interview.candidate?.last_name || ''}`.trim()
    const positionTitle = interview.position?.title || (typeof interview.position === 'string' ? interview.position : '')
    const interviewName = interview.name || ''
    const matchesSearch = candidateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         positionTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         interviewName.toLowerCase().includes(searchQuery.toLowerCase())
    
    let matchesStatus = false
    if (statusFilter === 'all') {
      matchesStatus = true
    } else if (statusFilter === 'draft') {
      matchesStatus = interview.is_draft === true
    } else {
      matchesStatus = interview.status === statusFilter && !interview.is_draft
    }
    
    const matchesType = typeFilter === 'all' || interview.type === typeFilter || interview.format === typeFilter
    
    return matchesSearch && matchesStatus && matchesType
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading interviews...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Interviews</h1>
          <p className="text-gray-600 mt-1">Schedule and manage candidate interviews</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button onClick={handleScheduleNew} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Schedule Interview
          </button>
          <button onClick={handleScheduleFullPage} className="btn-secondary">
            <ExternalLink className="w-4 h-4 mr-2" />
            Schedule Interview Fullpage
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(interviews || []).filter(i => i.status === 'scheduled' && !i.is_draft).length}
              </div>
              <div className="text-sm text-gray-600">Scheduled</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(interviews || []).filter(i => i.is_draft).length}
              </div>
              <div className="text-sm text-gray-600">Drafts</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(interviews || []).filter(i => {
                  const interviewDate = i.date || (i.scheduled_at ? i.scheduled_at.split('T')[0] : null)
                  return interviewDate && new Date(interviewDate).toDateString() === new Date().toDateString()
                }).length}
              </div>
              <div className="text-sm text-gray-600">Today</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(interviews || []).filter(i => i.status === 'completed').length}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(interviews || []).filter(i => i.status === 'cancelled' || i.status === 'no-show').length}
              </div>
              <div className="text-sm text-gray-600">Cancelled/No Show</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by candidate, position, or interview name..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                style={{ color: '#111827', backgroundColor: '#ffffff' }}
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            style={{ color: '#111827', backgroundColor: '#ffffff' }}
          >
            <option value="all">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            style={{ color: '#111827', backgroundColor: '#ffffff' }}
          >
            <option value="all">All Types</option>
            <option value="video">Video</option>
            <option value="phone">Phone</option>
            <option value="in-person">In Person</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {filteredInterviews.length} Interviews
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interview Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInterviews.map((interview) => (
                <motion.tr 
                  key={interview.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleRowClick(interview.id)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {interview.candidateName ? interview.candidateName.split(' ').map(n => n[0]).join('') : '?'}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{interview.candidateName || 'Unknown Candidate'}</div>
                        <div className="text-sm text-gray-500">{interview.candidateEmail || 'No email'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {interview.position?.title || (typeof interview.position === 'string' ? interview.position : 'No position')}
                    </div>
                    <div className="text-sm text-gray-500">{interview.stage || 'No stage'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {interview.name || interview.title || 'No name'}
                    </div>
                    <div className="text-sm text-gray-500">{interview.interview_type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDateTime(interview.date || '', interview.time || '').date}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDateTime(interview.date || '', interview.time || '').time} ({interview.duration}min)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(interview.type || interview.format)}
                      <span className="text-sm text-gray-900 capitalize">{interview.type || interview.format}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(interview.status, interview.is_draft)}`}>
                      {getStatusDisplay(interview.status, interview.is_draft)}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
